import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import B2BLayout from '../../../layouts/b2b'
import { Button, Form, Result, Space } from 'antd'
import { getColumns } from './columns'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import FiltersList from '../../../components/v2/filters'
import { filtersType } from '../../../components/v2/filters/interfaces'
import { getFilters as buildFilters } from './filters'
import { Sorter } from '../../../interfaces'
import { objectToUrl } from '../../utils'
import { FilterOutlined, PlusOutlined } from '@ant-design/icons'
import { currentFiltersProps, Enums, TablePaginationPosition } from './interface'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from 'react-redux'
import { getDataOfType } from '../../../../lib/utils/get-data-of-type'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TransferItem,
  TableSettingsButton,
} from '../../../components/v2/table-settings-button'
import { AppealsPrintButton } from '../ui/appeals-print-button/appeals-print-button'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import { FiltersFastButtons } from '../../../components/v2/filters-fast-buttons'
import DataTable from '../../../components/data-table'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import { HeaderAction } from '../../../contexts/header-actions-context/interface'
import { useMediaQuery } from 'react-responsive'
import { getMobileFields } from './mobile-fields'
import { ResultStatusType } from 'antd/lib/result'
import { AppealsOnboardingTour } from '../../../utilits/onboarding/tours/appeals-onboarding-tour'
import { resolveEnumCollectionIdByCode } from '../../../utilits/onboarding/open-enum-collection-by-code'
import { updateTourProgress } from '../../../store/session/action-creators'

const createMenuCounters = createSelector(
  (state: any) => state.menuV2,
  counters => counters,
)

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

const AppealsList: React.FC = () => {
  const [filtersForm] = Form.useForm()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const headerRef = useRef<HTMLDivElement | null>(null)
  const tableTourRef = useRef<HTMLDivElement | null>(null)
  const filtersTourRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerTourRef = useRef<HTMLDivElement | null>(null)
  const createButtonTourRef = useRef<HTMLButtonElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)
  const isInitialLoadRef = useRef(true)
  const isLoadingStagesRef = useRef(false)
  const isLoadingFiltersRef = useRef(false)
  const [data, setData] = useState([])
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    manager: [],
    stage: [],
    organization: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({
    phone: urlParams?.phone || undefined,
    manager: urlParams?.manager ? +urlParams.manager : undefined,
    id: urlParams.id ? +urlParams.id : undefined,
    organization: urlParams.organization ? +urlParams.organization : undefined,
    stage: urlParams?.stage ? urlParams.stage : undefined,
    dates: urlParams.dates || undefined,
    brand: urlParams?.brand || undefined,
    office: urlParams?.office || undefined,
  })
  const { sessionData } = useSelector(createSession)
  const dispatch = useDispatch()
  const isLeader = getDataOfType(sessionData, 'organization.id', Number, null) === 1
  const { setHeaderActions } = useHeaderActions()
  // тур "первичной настройки справочника" заменён на полноценный Tour для /appeals

  const [loading, setLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [stages, setStages] = useState<any>(null)
  const [sorter, setSorter] = useState<Sorter>({})
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))

  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('appealsPageSize')
    const parsedPageSize = Number(savedPageSize)
    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })

  const { counters } = useSelector(createMenuCounters)

  const officeId = sessionData?.user?.office?.id
  const isAxiomaOffice = officeId === 1

  const [localCounter, setLocalCounter] = useState<null | number>(null)
  const isMobile = useMediaQuery({ maxWidth: 768 })
  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  const areObjectsIdentical = (obj1: object, obj2: object): boolean => {
    if (obj1 === obj2) return true

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
      return false
    }

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length) return false

    for (const key of keys1) {
      if (!keys2.includes(key) || !areObjectsIdentical(obj1[key], obj2[key])) {
        return false
      }
    }

    return true
  }

  useEffect(() => {
    // устанавливает в локальную переменную текущее состояние счетчиков
    const newCounterData = counters?.['/appeals']
    if (newCounterData && !localCounter) {
      setLocalCounter(newCounterData)
    }
    // устанавливает в локальную переменную обновленное состояние счетчиков и подгружает свежие данные, если нужный счетчик обновился
    if (newCounterData && localCounter && newCounterData !== localCounter) {
      setLocalCounter(newCounterData)
      getData()
    }
  }, [counters])

  // прогресс тура /appeals хранится в jsonSettings.tours['tour.appeals.v1']

  useEffect(() => {
    const params = new URLSearchParams(search)
    const nextFilters: currentFiltersProps = {
      phone: params.get('phone') || undefined,
      manager: params.get('manager') ? Number(params.get('manager')) : undefined,
      id: params.get('id') ? Number(params.get('id')) : undefined,
      organization: params.get('organization') ? Number(params.get('organization')) : undefined,
      stage: params.get('stage') || undefined,
      dates: params.get('dates') || undefined,
      brand: params.get('brand') || undefined,
      office: params.get('office') || undefined,
    }
    setCurrentFilters(nextFilters)
  }, [search])

  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(() => {
    return [
      {
        href: location.pathname,
        title: 'Обращения',
      },
    ]
  }, [])

  useEffect(() => {
    if (!stages) {
      getStages()
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0 && isInitialLoadRef.current) {
      getAvailableFilters()
      isInitialLoadRef.current = false
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0) {
      getData()
    }
  }, [search, stages])

  /**
   * Получает стадии
   */
  const getStages = useCallback(() => {
    if (!stages && !isLoadingStagesRef.current) {
      isLoadingStagesRef.current = true
      fetchAPI('/api/get-stages/appeal')
        .then(data => {
          setStages(data)
          isLoadingStagesRef.current = false
        })
        .catch(() => {
          console.error('Не удалось получить стадии')
          setError({ code: 'error', message: 'Не удалось получить стадии' })
          isLoadingStagesRef.current = false
        })
    }
  }, [stages])

  /**
   * Удаляет запись
   */
  const deleteRecord = useCallback(
    currentId => {
      fetchAPI(`/api/appeal/${currentId}`, { method: 'DELETE' })
        .then(() => {
          const urlParamsObj = Object.fromEntries(new URLSearchParams(search))
          fetchAPI(`/api/list/appeal${objectToUrl(urlParamsObj)}`)
            .then(({ data, pagination: paginationData, sorter: sorterData }) => {
              if (data && stages) {
                setData(
                  data.map(item => ({
                    ...item,
                    stage: stages.find(stage => stage.code === item.stage),
                  })),
                )
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
                if (sorterData) {
                  setSorter(sorterData)
                }
              }
            })
            .catch(() => {
              console.error('Не удалось обновить список после удаления')
            })
        })
        .catch(() => {
          console.error('Не удалось удалить запись')
        })
    },
    [search, stages],
  )

  interface IOrganization {
    value: string
    id: number
  }
  interface IStage {
    name: string
    code: string
  }
  interface IManager {
    value: string
    id: number
  }

  /**
   * Получает список доступных фильтров для обращений
   */
  const getAvailableFilters = useCallback(
    async (currentStages = null) => {
      if (!stages || stages.length === 0 || isLoadingFiltersRef.current) {
        return
      }
      isLoadingFiltersRef.current = true
      try {
        const enumsList = { types: ['organizations', 'managers', 'brands', 'offices'] }
        const data = await fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)

        const stagesToUse = currentStages || stages

        const filters = {
          organization:
            data?.organizations?.map((i: IOrganization) => ({ label: i.value, value: i.id })) || [],
          stage:
            stagesToUse
              ?.filter((i: IStage) => i.code !== 'cancelled')
              ?.map((i: IStage) => ({ label: i.name, value: i.code })) || [],
          manager: data?.managers?.map((i: IManager) => ({ label: i.value, value: i.id })) || [],
          brand: data?.brands?.map((i: IManager) => ({ label: i.value, value: i.id })) || [],
          office: data?.offices?.map((i: IManager) => ({ label: i.value, value: i.id })) || [],
        }

        setAvailableFilters(filters)
        isLoadingFiltersRef.current = false
      } catch (err) {
        console.error({ err }, 'Не удалось получить список доступных фильтров')
        isLoadingFiltersRef.current = false
      }
    },
    [stages],
  )

  /**
   * Получает записи
   */
  const getData = useCallback(async () => {
    if (!stages || stages.length === 0) {
      return
    }
    try {
      setLoading(true)
      setError(null)

      const urlParamsObj = Object.fromEntries(new URLSearchParams(search))
      await fetchAPI(`/api/list/appeal${objectToUrl(urlParamsObj)}`).then(
        ({ data, pagination, sorter }) => {
          if (data) {
            setData(
              data.map(item => {
                return {
                  ...item,
                  stage: stages.find(stage => stage.code === item.stage),
                }
              }),
            )
            setPagination(prevState => ({
              ...prevState,
              ...pagination,
            }))
            setSorter(sorter)
          }
        },
      )
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
      setError({
        message: (error as IHttpError)?.message || 'Неизвестная ошибка',
        code: ((error as IHttpError)?.statusCode || 'error') as ResultStatusType,
      })
    } finally {
      setLoading(false)
    }
  }, [stages, search])

  /**
   * Отслеживает изменение состояния таблицы
   *
   * @param pagination
   * @param _filters
   * @param sorter
   */
  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
      setPagination(prevState => ({ ...prevState, ...pagination }))

      const current: Record<string, any> = {
        ...currentUrlParams,
        sort_order: sorter?.order,
        sort_field: sorter?.field,
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (current?.page === 1) {
        delete current.page
      }
      if (current?.pageSize === 10) {
        delete current.pageSize
      }
      if (current?.sort_order === undefined) {
        delete current.sort_order
        delete current.sort_field
      }
      const urlParams = new URLSearchParams(current).toString()
      navigate({ search: urlParams })
    },
    [search, navigate, pagination],
  )

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('measurementsPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 10) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  /**
   * Отправляет на страницу деталки
   * @param record
   */
  const handleRowClick = record => {
    const { id } = record
    navigate(`/appeal/${id}`)
  }

  /**
   * Отслеживает состояние фильтров
   * @param filters
   */
  const changeFiltersHandler = useCallback(
    filters => {
      const tmpFilter: Record<string, any> = { ...(filters || {}), pageSize: pagination.pageSize }
      if (tmpFilter.stage === '' || tmpFilter.stage === undefined || tmpFilter.stage === null) {
        delete tmpFilter.stage
      }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
    },
    [pagination],
  )

  const handleCreateNewClick = useCallback(() => {
    navigate('/appeal/new')
  }, [])

  const openSourceAppealCreateDictionary = useCallback(async () => {
    const id = await resolveEnumCollectionIdByCode('case_source_appeal_create')
    if (!id) return
    navigate(`/admin/enum-collection/${id}`, {
      state: {
        onboardingTourId: 'setup.case_source_appeal_create',
        onboardingCollectionCode: 'case_source_appeal_create',
      },
    })
  }, [navigate])

  const goToCreateAppeal = useCallback(async () => {
    // стартуем следующую часть тура на странице создания
    dispatch(
      updateTourProgress('tour.appeals.create.v1', {
        stage: 'prompted',
        currentStep: 0,
        completed: false,
      }) as any,
    )
    navigate('/appeal/new')
  }, [dispatch, navigate])

  // Если пользователь заполнил справочник вручную (без перехода из тура) — проверим и отметим prerequisite выполненным.
  useEffect(() => {
    const userId = sessionData?.user?.id
    if (!userId) return
    const tours = (sessionData as any)?.jsonSettings?.tours || {}
    if (tours?.['setup.case_source_appeal_create']?.completed) return
    ;(async () => {
      const collectionId = await resolveEnumCollectionIdByCode('case_source_appeal_create')
      if (!collectionId) return
      const resp = await fetchAPI(`/api/enum-collection/${collectionId}`)
      const hasValues = Array.isArray(resp?.data) && resp.data.length > 0
      if (hasValues) {
        dispatch(
          updateTourProgress('setup.case_source_appeal_create', {
            stage: 'completed',
            completed: true,
          }) as any,
        )
      }
    })().catch(() => {
      // ignore
    })
  }, [sessionData?.user?.id, sessionData, dispatch])

  const filters: filtersType[] = useMemo(
    () => buildFilters({ currentFilters, availableFilters, isAxiomaOffice }),
    [currentFilters, availableFilters, isAxiomaOffice],
  )

  const columns = useMemo(() => getColumns(isLeader, sorter), [isLeader, sorter])
  const viewColumns = useMemo(() => {
    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        // @ts-ignore
        const item = columns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }

      const actionColumn = columns.find((column: any) => column.key === 'operation')
      if (actionColumn && !result.find((col: any) => col?.key === 'operation')) {
        result.push(actionColumn)
      }

      return result
    }
    return columns
  }, [columns, jsonSettings])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/list/appeal', new URLSearchParams(search))
  }, [search])

  const settingsColumns = useMemo(
    () =>
      columns.map(column => ({
        // @ts-ignore
        key: column?.dataIndex,
        title: column?.title,
      })),
    [columns],
  ) as TransferItem[]

  const mobileFields = useMemo(() => getMobileFields(), [])

  const fastButtons = useMemo(() => {
    const today = dayjs().format('YYYY.MM.DD')
    const todayRange = `${today}-${today}`
    const currentUserId = sessionData?.user?.id

    const baseButtons: { name: string; code: string; value: string }[] = [
      { name: 'Сегодня', code: 'dates', value: todayRange },
      { name: 'Новые', code: 'stage', value: 'new' },
      { name: 'Выполнены', code: 'stage', value: 'done' },
    ]

    if (currentUserId) {
      return [
        { name: 'Мои обращения', code: 'manager', value: String(currentUserId) },
        ...baseButtons,
      ]
    }

    return baseButtons
  }, [sessionData?.user?.id])

  useEffect(() => {
    const actions: HeaderAction[] = []

    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }

    setHeaderActions(actions)

    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, settingsColumns, isMobile])

  const dataWithActions = useMemo(() => {
    return data.map(item => ({
      ...(item as any),
      deleteRecord,
    }))
  }, [data, deleteRecord])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton pageTitle={'Обращения'}>
      <AppealsOnboardingTour
        pathname={pathname}
        menuAppealsTarget={() =>
          (document.querySelector('[data-tour-id="menu-appeals"]') as HTMLElement) || document.body
        }
        tableTarget={() => tableTourRef.current || document.body}
        filtersTarget={() => filtersDrawerTourRef.current || document.body}
        createButtonTarget={() => createButtonTourRef.current || document.body}
        openSourceAppealCreateDictionary={openSourceAppealCreateDictionary}
        goToCreateAppeal={goToCreateAppeal}
      />
      {error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!error && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <Button ref={createButtonTourRef as any} onClick={handleCreateNewClick}>
                    <PlusOutlined />
                    Оформить обращение
                  </Button>
                  <div ref={filtersTourRef}>
                    <FiltersList
                      filters={filters.filter(filter => ['id', 'stage'].includes(filter.name))}
                      onChange={changeFiltersHandler}
                      form={filtersForm}
                    />
                  </div>
                </Space>
                <div ref={filtersDrawerTourRef}>
                  <FiltersDrawer
                    filters={filters}
                    onChange={changeFiltersHandler}
                    currentFilters={currentFilters}
                    ref={filtersDrawerRef}
                  />
                </div>
              </Space>
            }
            persistent={
              isMobile ? (
                <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
              ) : (
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align={'center'}
                  wrap
                >
                  <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
                  <Space align={'center'} wrap>
                    <AppealsPrintButton stages={stages} columns={viewColumns} path={excelPath} />
                    <TableSettingsButton items={settingsColumns} ref={tableSettingsRef as any} />
                  </Space>
                </Space>
              )
            }
          />
          <div ref={tableTourRef}>
            <DataTable
              columns={viewColumns}
              mobileFields={mobileFields}
              data={dataWithActions}
              loading={loading}
              rowKey={r => String(r.id)}
              highlightLastRowPrefix={'appeal'}
              pagination={pagination}
              onRowClick={handleRowClick}
              onPageChange={handlePageChange}
              onTableChange={handleTableChange}
              className={'appeals-table'}
              headerRef={headerRef}
              maxLines={1}
              bordered
              scroll={{ x: 'max-content' }}
            />
          </div>
        </>
      )}
    </B2BLayout>
  )
}

export default AppealsList
